import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Table,
  Tag,
  Drawer,
  Descriptions,
  Divider,
  Progress,
  Spin,
  Tooltip,
} from "antd";
import { motion } from "framer-motion";
import { Card } from "reactstrap";
import Head from "../../layout/head/Head";
import Content from "../../layout/content/Content";
import {
  BlockHead,
  BlockHeadContent,
  BlockTitle,
} from "../../components/block/Block";
import { formatCurrency } from "../../utils/Utils";
import {
  DollarOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  WarningOutlined,
  BellOutlined,
  RobotOutlined,
  LoadingOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { ShimmerTableCard } from "../procurement-inventory-control-tower/components/ProcurementShimmerLoader";
import { render } from "@testing-library/react";
import dayjs from "dayjs";

interface WorkOrderExecution {
  key: string;
  project: string;
  woNumber: string;
  contractor: string;
  plannedStart: string;
  plannedEnd: string;
  actualProgress: number;
  delayDays: number;
  causeOfDelay: string;
  currentRA: string;
  woValue: number; // Work Order Value (₹1.2 Cr each)
  raValue: number; // RA Bill Value
  dailyCostRate: number; // Daily cost rate in ₹
  overhead: number; // Overhead in ₹
  riskImpact: number; // Calculated as: delayDays × (dailyCostRate + overhead)
  flag: "🔴" | "🟠" | "🟢";
}

interface RABillLifecycle {
  key: string;
  raNumber: string;
  woNumber: string;
  contractor: string;
  raPeriod: string;
  grossAmount: number;
  deductions: number;
  netPayable: number;
  submitted: string;
  approved: string;
  paid: string;
  agingDays: number;
  interestRate: number; // Interest rate: 2% per month
  interestCharged: number; // Interest charged based on aging days
  riskImpact: number; // Net Payable × interest charged on aging days
  flag: "🔴" | "🟠" | "🟢";
}

interface PaymentStatus {
  key: string;
  contractor: string;
  totalWOs: number;
  raSubmitted: number;
  approvedValue: number;
  paidValue: number;
  outstanding: number;
  percentPaid: number;
  avgPaymentDelay: number;
  riskImpact: number; // Total risk impact from all RA bills for this contractor
  raBills: RABillLifecycle[]; // List of RA bills for this contractor
  flag: "🔴" | "🟠" | "🟢";
}

interface RetentionExposure {
  key: string;
  woNumber: string;
  contractor: string;
  retentionPercent: number;
  accrued: number;
  released: number;
  balance: number;
  nextReleaseDate: string;
  flag: "🔴" | "🟠" | "🟢";
}

interface BudgetPerformance {
  key: string;
  project: string;
  budget: number;
  committed: number;
  accrued: number;
  paid: number;
  variance: number;
  utilization: number; // (Accrued ÷ Budget) × 100
  forecastOverrun: number;
  flag: "🔴" | "🟠" | "🟢";
}

interface WORiskInterlinking {
  woId: string;
  woNumber: string;
  contractor: string;
  delayDays: number;
  riskType: string;
  downstreamImpact: string;
  triggeredModule: string;
  monetaryImpact: number;
  automatedMitigation: string;
  automatedMitigationSteps?: Array<{
    step: string;
    details?: string[];
    notifications?: string[];
    costSavings?: string;
  }>;
  responsiblePerson: string;
  status: "Active" | "Mitigated" | "Pending";
}

const WorkOrderExecutionPaymentControlTower: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [woInterlinkingDrawerVisible, setWoInterlinkingDrawerVisible] =
    useState(false);
  const [selectedWORisks, setSelectedWORisks] = useState<WORiskInterlinking[]>(
    []
  );
  const [raRiskDrawerVisible, setRaRiskDrawerVisible] = useState(false);
  const [selectedRABill, setSelectedRABill] = useState<RABillLifecycle | null>(null);
  const [paymentRiskDrawerVisible, setPaymentRiskDrawerVisible] = useState(false);
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState<PaymentStatus | null>(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [aiLoadingWOs, setAiLoadingWOs] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [aiLoadingTexts, setAiLoadingTexts] = useState<{
    [key: string]: string;
  }>({});
  const [aiLoadingRABill, setAiLoadingRABill] = useState(false);
  const [aiLoadingRAText, setAiLoadingRAText] = useState("");
  const [aiLoadingPayment, setAiLoadingPayment] = useState(false);
  const [aiLoadingPaymentText, setAiLoadingPaymentText] = useState("");

  const aiLoadingMessages = [
    "Analyzing work order dependencies...",
    "Checking contractor performance...",
    "Fetching optimal solutions...",
    "Processing payment schedule...",
    "Calculating impact...",
    "Generating recommendations...",
  ];

  const raAiLoadingMessages = [
    "Analyzing RA Bill payment delays...",
    "Checking financial impact...",
    "Fetching approval workflows...",
    "Calculating interest charges...",
    "Processing mitigation strategies...",
    "Generating automated recommendations...",
  ];

  const paymentAiLoadingMessages = [
    "Analyzing contractor payment patterns...",
    "Calculating total interest exposure...",
    "Processing bulk approval strategies...",
    "Optimizing payment queue priority...",
    "Evaluating contractor relationship impact...",
    "Generating comprehensive mitigation plan...",
  ];

  // Mock data - Each WO value is ₹1.2 Cr
  const workOrderExecutions: WorkOrderExecution[] = [
    {
      key: "1",
      project: "Grandeur Heights",
      woNumber: "WO-2025-0012",
      contractor: "Shapoorji Pallonji & Co",
      plannedStart: "02/01/2025",
      plannedEnd: "03/31/2025",
      actualProgress: 65,
      delayDays: 7,
      causeOfDelay: "Cement Delay",
      currentRA: "RA-00023",
      woValue: 12000000, // Work Order Value: ₹1.2 Cr
      raValue: 810000, // RA Bill Value
      dailyCostRate: 15000, // ₹15K per day
      overhead: 3000, // ₹3K overhead
      // Risk Impact = 7 × (15000 + 3000) = 7 × 18000 = ₹1,26,000
      riskImpact: 126000,
      flag: "🔴",
    },
    {
      key: "2",
      project: "Paramount Rise",
      woNumber: "WO-2025-0045",
      contractor: "Godrej Interio",
      plannedStart: "02/10/2025",
      plannedEnd: "04/05/2025",
      actualProgress: 48,
      delayDays: 3,
      causeOfDelay: "Labor Shortage",
      currentRA: "RA-00031",
      woValue: 12000000, // Work Order Value: ₹1.2 Cr
      raValue: 475000, // RA Bill Value
      dailyCostRate: 22000, // ₹22K per day
      overhead: 3000, // ₹3K overhead
      // Risk Impact = 3 × (22000 + 3000) = 3 × 25000 = ₹75,000
      riskImpact: 75000,
      flag: "🟠",
    },
    {
      key: "3",
      project: "Elysium Towers",
      woNumber: "WO-2025-0067",
      contractor: "Larsen & Toubro Ltd",
      plannedStart: "03/01/2025",
      plannedEnd: "05/15/2025",
      actualProgress: 30,
      delayDays: 10,
      causeOfDelay: "Rebar Delay",
      currentRA: "RA-00027",
      woValue: 12000000, // Work Order Value: ₹1.2 Cr
      raValue: 320000, // RA Bill Value
      dailyCostRate: 25000, // ₹25K per day
      overhead: 3000, // ₹3K overhead
      // Risk Impact = 10 × (25000 + 3000) = 10 × 28000 = ₹2,80,000
      riskImpact: 280000,
      flag: "🔴",
    },
  ];

  const raBillLifecycles: RABillLifecycle[] = [
    {
      key: "1",
      raNumber: "RA-00023",
      woNumber: "WO-2025-0012",
      contractor: "Shapoorji Pallonji & Co",
      raPeriod: "Feb 2025",
      grossAmount: 810000,
      deductions: 145000,
      netPayable: 665000,
      submitted: "03/01/2025",
      approved: "03/08/2025",
      paid: "03/20/2025",
      agingDays: 19,
      interestRate: 2, // 2% per month
      // Interest charged = (agingDays / 30) × 2% = (19 / 30) × 2% = 1.27%
      interestCharged: 1.27,
      // Risk Impact = NetPayable × (interestCharged / 100) = 665000 × 0.0127 = ₹8,445
      riskImpact: 8445,
      flag: "🟢",
    },
    {
      key: "2",
      raNumber: "RA-00027",
      woNumber: "WO-2025-0067",
      contractor: "Larsen & Toubro Ltd",
      raPeriod: "Feb 2025",
      grossAmount: 320000,
      deductions: 85000,
      netPayable: 235000,
      submitted: "03/05/2025",
      approved: "Pending",
      paid: "---",
      agingDays: 15,
      interestRate: 2, // 2% per month
      // Interest charged = (agingDays / 30) × 2% = (15 / 30) × 2% = 1.0%
      interestCharged: 1.0,
      // Risk Impact = NetPayable × (interestCharged / 100) = 235000 × 0.01 = ₹2,350
      riskImpact: 2350,
      flag: "🔴",
    },
    {
      key: "3",
      raNumber: "RA-00031",
      woNumber: "WO-2025-0045",
      contractor: "Godrej Interio",
      raPeriod: "Mar 2025",
      grossAmount: 475000,
      deductions: 100000,
      netPayable: 375000,
      submitted: "03/20/2025",
      approved: "---",
      paid: "---",
      agingDays: 6,
      interestRate: 2, // 2% per month
      // Interest charged = (agingDays / 30) × 2% = (6 / 30) × 2% = 0.4%
      interestCharged: 0.4,
      // Risk Impact = NetPayable × (interestCharged / 100) = 375000 × 0.004 = ₹1,500
      riskImpact: 1500,
      flag: "🟠",
    },
  ];

  const paymentStatuses: PaymentStatus[] = [
    {
      key: "1",
      contractor: "Shapoorji Pallonji & Co",
      totalWOs: 8,
      raSubmitted: 6,
      approvedValue: 6820000,
      paidValue: 5580000,
      outstanding: 1240000,
      percentPaid: 81.8,
      avgPaymentDelay: 10,
      // Risk Impact: Sum of all RA bills for this contractor (only RA-00023)
      riskImpact: 8445,
      raBills: raBillLifecycles.filter(ra => ra.contractor === "Shapoorji Pallonji & Co"),
      flag: "🟠",
    },
    {
      key: "2",
      contractor: "Larsen & Toubro Ltd",
      totalWOs: 5,
      raSubmitted: 5,
      approvedValue: 4100000,
      paidValue: 3120000,
      outstanding: 980000,
      percentPaid: 76.1,
      avgPaymentDelay: 15,
      // Risk Impact: Sum of all RA bills for this contractor (only RA-00027)
      riskImpact: 2350,
      raBills: raBillLifecycles.filter(ra => ra.contractor === "Larsen & Toubro Ltd"),
      flag: "🔴",
    },
    {
      key: "3",
      contractor: "Godrej Interio",
      totalWOs: 3,
      raSubmitted: 2,
      approvedValue: 1850000,
      paidValue: 1850000,
      outstanding: 0,
      percentPaid: 100,
      avgPaymentDelay: 4,
      // Risk Impact: Sum of all RA bills for this contractor (only RA-00031)
      riskImpact: 1500,
      raBills: raBillLifecycles.filter(ra => ra.contractor === "Godrej Interio"),
      flag: "🟢",
    },
  ];

  const retentionExposures: RetentionExposure[] = [
    {
      key: "1",
      woNumber: "WO-2025-0012",
      contractor: "Shapoorji Pallonji & Co",
      retentionPercent: 5,
      accrued: 340000,
      released: 120000,
      balance: 220000,
      nextReleaseDate: "06/15/2025",
      flag: "🟠",
    },
    {
      key: "2",
      woNumber: "WO-2025-0045",
      contractor: "Godrej Interio",
      retentionPercent: 5,
      accrued: 180000,
      released: 0,
      balance: 180000,
      nextReleaseDate: "06/30/2025",
      flag: "🟠",
    },
    {
      key: "3",
      woNumber: "WO-2025-0067",
      contractor: "Larsen & Toubro Ltd",
      retentionPercent: 7,
      accrued: 295000,
      released: 0,
      balance: 295000,
      nextReleaseDate: "07/10/2025",
      flag: "🔴",
    },
  ];

  // Work Order Cost vs Budget Performance - Work Orders Module Only (47% of total project budget)
  const budgetPerformances: BudgetPerformance[] = [
    {
      key: "1",
      project: "Grandeur Heights",
      budget: 23500000, // Work Orders Module: 47% of ₹5.00 Cr = ₹2.35 Cr
      committed: 21620000, // 47% of total committed (₹4.60 Cr) = ₹2.16 Cr
      accrued: 17860000, // 47% of total accrued (₹3.80 Cr) = ₹1.79 Cr
      paid: 16215000, // 47% of total paid (₹3.45 Cr) = ₹1.62 Cr
      variance: 5640000, // Budget - Accrued = ₹0.56 Cr
      utilization: 76, // (Accrued ÷ Budget) × 100 = 76%
      forecastOverrun: 0, // ₹0.00
      flag: "🟢",
    },
    {
      key: "2",
      project: "Paramount Rise",
      budget: 17860000, // Work Orders Module: 47% of ₹3.80 Cr = ₹1.79 Cr
      committed: 17625000, // 47% of total committed (₹3.75 Cr) = ₹1.76 Cr
      accrued: 16685000, // 47% of total accrued (₹3.55 Cr) = ₹1.67 Cr
      paid: 15040000, // 47% of total paid (₹3.20 Cr) = ₹1.50 Cr
      variance: 1175000, // Budget - Accrued = ₹0.12 Cr
      utilization: 93, // (Accrued ÷ Budget) × 100 = 93%
      forecastOverrun: 500000, // ₹5.00L
      flag: "🟠",
    },
    {
      key: "3",
      project: "Elysium Towers",
      budget: 35250000, // Work Orders Module: 47% of ₹7.50 Cr = ₹3.53 Cr
      committed: 33840000, // 47% of total committed (₹7.20 Cr) = ₹3.38 Cr
      accrued: 33135000, // 47% of total accrued (₹7.05 Cr) = ₹3.31 Cr
      paid: 28670000, // 47% of total paid (₹6.10 Cr) = ₹2.87 Cr
      variance: 2115000, // Budget - Accrued = ₹0.21 Cr
      utilization: 94, // (Accrued ÷ Budget) × 100 = 94%
      forecastOverrun: 1500000, // ₹15.00L
      flag: "🔴",
    },
    {
      key: "4",
      project: "Skyline Royale",
      budget: 21150000, // Work Orders Module: 47% of ₹6.00 Cr = ₹2.82 Cr
      committed: 19740000, // 47% of ₹5.63 Cr = ₹2.65 Cr
      accrued: 16920000, // 47% of ₹5.15 Cr = ₹2.42 Cr
      paid: 14570000, // 47% of ₹4.62 Cr = ₹2.17 Cr
      variance: 4230000, // Budget - Accrued = ₹0.40 Cr
      utilization: 80, // (Accrued ÷ Budget) × 100 = 86%
      forecastOverrun: 0, // ₹8.00L
      flag: "🟢",
    },
    {
      key: "5",
      project: "Opéra Sky",
      budget: 15040000, // Work Orders Module: 47% of ₹4.37 Cr = ₹2.06 Cr
      committed: 14100000, // 47% of ₹4.02 Cr = ₹1.89 Cr
      accrued: 13160000, // 47% of ₹3.55 Cr = ₹1.67 Cr
      paid: 11750000, // 47% of ₹3.20 Cr = ₹1.50 Cr
      variance: 1880000, // Budget - Accrued = ₹0.39 Cr
      utilization: 87, // (Accrued ÷ Budget) × 100 = 81%
      forecastOverrun: 235000, // ₹0.00
      flag: "🟠",
    },
  ];

  const mockWORiskData: { [woNumber: string]: WORiskInterlinking[] } = {
    "WO-2025-0012": [
      {
        woId: "WO-001",
        woNumber: "WO-2025-0012",
        contractor: "Shapoorji Pallonji & Co",
        delayDays: 7,
        riskType: "Material Delay (Cement)",
        downstreamImpact:
          "Foundation & RCC Work Delayed → RA Billing Delayed → Payment Postponed",
        triggeredModule: "Procurement, RA Billing, Payment",
        monetaryImpact: 125000,
        automatedMitigation:
          "Expedite material supply + Adjust RA billing schedule + Fast-track payment approval",
        automatedMitigationSteps: [
          {
            step: "Expedite Material Supply",
            details: [
              "Material: Cement OPC 53",
              "Shortage Quantity: 350 bags",
              "Site Transfer: 450 bags from Villa Cluster (ETA: Today 5 PM)",
              "Supplier Order: 120 bags from UltraTech (ETA: 2025-10-15)",
              "Work Resumption: Tomorrow morning",
            ],
            notifications: [
              "Notify Shapoorji Pallonji & Co Site Engineer - Material arrangement confirmed",
              "Update WO-2025-0012 timeline - 7-day delay mitigation in progress",
              "Alert Procurement Manager - Material transfer + supplier order tracking",
            ],
          },
          {
            step: "Adjust RA Billing Schedule",
            details: [
              "Current RA: RA-00023 (submitted, approved, paid)",
              "Next RA Period: October 2025 (due for submission: 2025-11-05)",
              "Revised Submission: 2025-11-12 (7-day delay adjustment)",
              "Expected Work Completion: 95% by revised RA date",
              "RA Value Estimate: ₹9.5L (based on projected completion)",
            ],
            notifications: [
              "Notify RA Team - Update billing schedule for WO-2025-0012",
              "Alert Finance Team - Payment timeline adjusted by 7 days",
              "Inform Shapoorji Pallonji & Co - RA submission date revised to 2025-11-12",
            ],
          },
          {
            step: "Fast-Track Payment Approval",
            details: [
              "Outstanding Amount: ₹6.65L (RA-00023, already paid)",
              "Next Payment: ₹9.5L (projected RA-00024)",
              "Approval SLA: 7 days (standard) → Fast-track to 3 days",
              "Payment Release: Within 5 days post-approval",
              "Contractor Cash Flow: Maintained to avoid site slowdown",
            ],
            notifications: [
              "Escalate to CFO - Fast-track approval for WO-2025-0012 payments",
              "Alert Finance Approver - High-priority payment processing",
              "Notify Shapoorji Pallonji & Co - Payment commitment for cash flow continuity",
            ],
            costSavings:
              "₹1.85L (avoided extended delay penalties + contractor efficiency maintained)",
          },
        ],
        responsiblePerson: "Project Director",
        status: "Active",
      },
    ],
    "WO-2025-0045": [
      {
        woId: "WO-002",
        woNumber: "WO-2025-0045",
        contractor: "Godrej Interio",
        delayDays: 3,
        riskType: "Labor Shortage",
        downstreamImpact:
          "Interior Work Delayed → RA Progress Reduced → Client Handover Risk",
        triggeredModule: "Work Execution, RA Billing",
        monetaryImpact: 75000,
        automatedMitigation:
          "Reallocate crew from other sites + Adjust RA billing",
        automatedMitigationSteps: [
          {
            step: "Reallocate Labor Resources",
            details: [
              "Current Labor: 8 workers (60% utilization)",
              "Required Labor: 15 workers (for schedule recovery)",
              "Inter-Site Transfer: 7 workers from completed Opéra Sky project",
              "Transfer Timeline: Available from 2025-10-15",
              "Work Acceleration: 3-day delay recovery within 10 days",
            ],
            notifications: [
              "Notify Godrej Interio Project Manager - Labor augmentation confirmed",
              "Alert Site Supervisor - 7 additional workers reporting on 2025-10-15",
              "Update WO-2025-0045 schedule - Recovery plan in effect",
            ],
          },
          {
            step: "Adjust RA Billing & Progress Tracking",
            details: [
              "Current RA: RA-00031 (submitted, awaiting approval)",
              "Current Progress: 48% (delayed by 3 days)",
              "Revised Progress Target: 65% by 2025-10-25 (with labor augmentation)",
              "RA Approval: Expedite to release contractor payment (₹3.75L)",
              "Next RA Period: October 2025 (projected value: ₹5.2L)",
            ],
            notifications: [
              "Fast-track RA-00031 approval - 6 days aging, pending approval",
              "Notify Finance Team - Expedite ₹3.75L payment to Godrej Interio",
              "Alert RA Team - Monitor progress recovery for next billing cycle",
            ],
            costSavings:
              "₹75K (avoided project delay penalties + maintained client timeline)",
          },
        ],
        responsiblePerson: "Project Manager",
        status: "Active",
      },
    ],
    "WO-2025-0067": [
      {
        woId: "WO-003",
        woNumber: "WO-2025-0067",
        contractor: "Larsen & Toubro Ltd",
        delayDays: 10,
        riskType: "Material Delay (Rebar) + RA Approval Delay",
        downstreamImpact:
          "Structural Work Stopped → Contractor Cash Flow Strain → Site Progress Slowdown → Budget Overrun Risk",
        triggeredModule: "Procurement, RA Approval, Payment, Project Budget",
        monetaryImpact: 280000,
        automatedMitigation:
          "Expedite material + Fast-track RA approval + Emergency payment release + Budget review",
        automatedMitigationSteps: [
          {
            step: "Expedite Material Supply (Rebar)",
            details: [
              "Material: 8mm Rebar (TMT Steel)",
              "Shortage Quantity: 15 tons",
              "Site Transfer: 6.5 tons from Road Infra Project (ETA: Tomorrow 10 AM)",
              "Supplier Order: 8.5 tons from Shree TMT (ETA: 2025-10-20)",
              "Work Resumption: 2025-10-12 (partial), full capacity by 2025-10-22",
            ],
            notifications: [
              "Notify Larsen & Toubro Ltd - Material arrangement in progress",
              "Alert Procurement Head - Dual-track mitigation (transfer + procurement)",
              "Update WO-2025-0067 schedule - 10-day delay recovery plan active",
            ],
          },
          {
            step: "Fast-Track RA Approval (RA-00027)",
            details: [
              "RA Number: RA-00027",
              "Status: Submitted on 2025-03-05, pending approval (15 days aging)",
              "Net Payable: ₹2.35L",
              "Approval SLA: Standard 7 days → Escalated to 1 day (emergency)",
              "Reason: Contractor cash flow strain due to 10-day work stoppage",
              "Approval Target: 2025-10-10 (emergency CFO approval)",
            ],
            notifications: [
              "Escalate to CFO - Emergency RA approval for WO-2025-0067",
              "Alert Finance Approver - High-priority contractor cash flow risk",
              "Notify Larsen & Toubro Ltd - RA approval & payment in 48 hours",
            ],
          },
          {
            step: "Emergency Payment Release",
            details: [
              "Payment Amount: ₹2.35L (RA-00027)",
              "Payment Mode: Immediate release (within 24 hours post-approval)",
              "Contractor Outstanding: ₹9.8L (total across all WOs)",
              "Cash Flow Support: Priority payment to avoid site shutdown",
              "Payment Commitment: Next RA (October 2025) also fast-tracked",
            ],
            notifications: [
              "Notify Finance Team - Process ₹2.35L payment immediately",
              "Alert Treasury - Ensure funds availability for emergency release",
              "Inform Larsen & Toubro Ltd - Payment confirmation & schedule commitment",
            ],
          },
          {
            step: "Budget Review & Overrun Mitigation",
            details: [
              "Current Budget Variance: ₹7L (10.4% overrun on Elysium Towers)",
              "Delay Impact: Additional ₹2.8L risk if unresolved",
              "Mitigation Action: Freeze new WO issuance until recovery",
              "Schedule Recovery: 10-day delay absorbed over 30 days (accelerated execution)",
              "Cost Control: Monitor daily progress to prevent further overrun",
            ],
            notifications: [
              "Alert Project Director - Budget overrun risk on Elysium Towers",
              "Notify PMO - WO freeze + recovery plan for Elysium Towers",
              "Schedule review meeting with Larsen & Toubro Ltd (2025-10-18)",
            ],
            costSavings:
              "₹2.8L (avoided extended delay + budget overrun mitigation)",
          },
        ],
        responsiblePerson: "Project Director + CFO",
        status: "Active",
      },
    ],
  };

  useEffect(() => {
    setTimeout(() => setLoading(false), 1500);
  }, []);

  // AI Loading effect for RA Bill drawer
  useEffect(() => {
    if (raRiskDrawerVisible && selectedRABill) {
      // Start loading
      setAiLoadingRABill(true);
      setAiLoadingRAText(raAiLoadingMessages[0]);

      // Cycle through loading messages
      let messageIndex = 0;
      const messageInterval = setInterval(() => {
        messageIndex = (messageIndex + 1) % raAiLoadingMessages.length;
        setAiLoadingRAText(raAiLoadingMessages[messageIndex]);
      }, 800);

      // Complete loading after 2.5 seconds
      const loadingTimeout = setTimeout(() => {
        clearInterval(messageInterval);
        setAiLoadingRABill(false);
      }, 2500);

      // Cleanup on unmount or when drawer closes
      return () => {
        clearInterval(messageInterval);
        clearTimeout(loadingTimeout);
      };
    }
  }, [raRiskDrawerVisible, selectedRABill]);

  // AI Loading effect for Payment Status drawer
  useEffect(() => {
    if (paymentRiskDrawerVisible && selectedPaymentStatus) {
      // Start loading
      setAiLoadingPayment(true);
      setAiLoadingPaymentText(paymentAiLoadingMessages[0]);

      // Cycle through loading messages
      let messageIndex = 0;
      const messageInterval = setInterval(() => {
        messageIndex = (messageIndex + 1) % paymentAiLoadingMessages.length;
        setAiLoadingPaymentText(paymentAiLoadingMessages[messageIndex]);
      }, 800);

      // Complete loading after 2.5 seconds
      const loadingTimeout = setTimeout(() => {
        clearInterval(messageInterval);
        setAiLoadingPayment(false);
      }, 2500);

      // Cleanup on unmount or when drawer closes
      return () => {
        clearInterval(messageInterval);
        clearTimeout(loadingTimeout);
      };
    }
  }, [paymentRiskDrawerVisible, selectedPaymentStatus]);

  const executionColumns = [
    {
      title: "Project",
      dataIndex: "project",
      key: "project",
      width: 150,
      render: (text: string) => <span style={{ fontWeight: 600 }}>{text}</span>,
    },
    {
      title: "WO #",
      dataIndex: "woNumber",
      key: "woNumber",
      width: 130,
    },
    {
      title: "Contractor",
      dataIndex: "contractor",
      key: "contractor",
      width: 150,
    },
    {
      title: "Planned Start",
      dataIndex: "plannedStart",
      key: "plannedStart",
      width: 110,
      align: "center" as const,
      render: (text: string) => (
        <span>{text ? dayjs(text).format("DD-MMM-YYYY") : "-"}</span>
      ),
    },
    {
      title: "Planned End",
      dataIndex: "plannedEnd",
      key: "plannedEnd",
      width: 110,
      align: "center" as const,
      render: (text: string) => (
        <span>{text ? dayjs(text).format("DD-MMM-YYYY") : "-"}</span>
      ),
    },
    {
      title: "Actual Progress %",
      dataIndex: "actualProgress",
      key: "actualProgress",
      width: 150,
      render: (val: number) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Progress
            percent={val}
            size="small"
            strokeColor={
              val >= 80 ? "#52c41a" : val >= 50 ? "#faad14" : "#ff4d4f"
            }
            style={{ width: 80 }}
          />
          {/* <span style={{ fontSize: 12, fontWeight: 600 }}>{val}%</span> */}
        </div>
      ),
    },
    {
      title: "Delay (Days)",
      dataIndex: "delayDays",
      key: "delayDays",
      width: 100,
      align: "center" as const,
      render: (val: number, record: WorkOrderExecution) => (
        <Tag
          color={val >= 7 ? "red" : val >= 3 ? "orange" : "green"}
         
        >
          {val}
        </Tag>
      ),
    },
    {
      title: "Cause of Delay",
      dataIndex: "causeOfDelay",
      key: "causeOfDelay",
      width: 130,
    },
    {
      title: "Current RA #",
      dataIndex: "currentRA",
      key: "currentRA",
      width: 110,
    },
    {
      title: "RA Value (₹)",
      dataIndex: "raValue",
      key: "raValue",
      width: 120,
      align: "right" as const,
      render: (val: number) => (
        <span style={{ fontWeight: 600, color: "#1890ff" }}>
          {formatCurrency(val, "INR")}
        </span>
      ),
    },
    {
      title: "Daily Cost Rate (₹)",
      dataIndex: "dailyCostRate",
      key: "dailyCostRate",
      width: 140,
      align: "right" as const,
      render: (val: number) => (
        <span style={{ fontWeight: 600, color: "#595959" }}>
          {formatCurrency(val, "INR")}
        </span>
      ),
    },
    {
      title: "Overhead (₹)",
      dataIndex: "overhead",
      key: "overhead",
      width: 110,
      align: "right" as const,
      render: (val: number) => (
        <span style={{ fontWeight: 600, color: "#595959" }}>
          {formatCurrency(val, "INR")}
        </span>
      ),
    },
    {
      title: "Risk Impact (₹)",
      dataIndex: "riskImpact",
      key: "riskImpact",
      width: 120,
      align: "right" as const,
      render: (val: number, record: WorkOrderExecution) => (
        <span style={{ fontWeight: 600, color: "#ff4d4f",cursor: "pointer", textDecoration: "underline" }}
          onClick={(e) => {
            e.stopPropagation();
            const woRisks = mockWORiskData[record.woNumber] || [];
            setSelectedWORisks(woRisks);
            setWoInterlinkingDrawerVisible(true);
          }}>
          {formatCurrency(val, "INR")}
        </span>
      ),
    },
    {
      title: "Flag",
      dataIndex: "flag",
      key: "flag",
      width: 80,
      align: "center" as const,
      render: (flag: string) => {
        const getTooltip = () => {
          if (flag === "🔴") return "Delay >7 days or Risk >₹1L";
          if (flag === "🟠") return "Delay 3-6 days";
          return "Delay <3 days";
        };
        return (
          <Tooltip title={getTooltip()}>
            <span style={{ fontSize: 18, cursor: "help" }}>{flag}</span>
          </Tooltip>
        );
      },
    },
  ];

  const raBillColumns = [
    {
      title: "RA #",
      dataIndex: "raNumber",
      key: "raNumber",
      width: 110,
      render: (text: string) => <span style={{ fontWeight: 600 }}>{text}</span>,
    },
    {
      title: "WO #",
      dataIndex: "woNumber",
      key: "woNumber",
      width: 130,
    },
    {
      title: "Contractor",
      dataIndex: "contractor",
      key: "contractor",
      width: 150,
    },
    {
      title: "RA Period",
      dataIndex: "raPeriod",
      key: "raPeriod",
      width: 100,
      align: "center" as const,
    },
    {
      title: "Gross (₹)",
      dataIndex: "grossAmount",
      key: "grossAmount",
      width: 120,
      align: "right" as const,
      render: (val: number) => <span>{formatCurrency(val, "INR")}</span>,
    },
    {
      title: "Deductions (₹)",
      dataIndex: "deductions",
      key: "deductions",
      width: 120,
      align: "right" as const,
      render: (val: number) => (
        <span style={{ color: "#ff4d4f" }}>{formatCurrency(val, "INR")}</span>
      ),
    },
    {
      title: "Net Payable (₹)",
      dataIndex: "netPayable",
      key: "netPayable",
      width: 130,
      align: "right" as const,
      render: (val: number) => (
        <span style={{ fontWeight: 600, color: "#52c41a" }}>
          {formatCurrency(val, "INR")}
        </span>
      ),
    },
    {
      title: "Submitted",
      dataIndex: "submitted",
      key: "submitted",
      width: 110,
      align: "center" as const,
      render: (text: string) => (
        <span>{text ? dayjs(text).format("DD-MMM-YYYY") : "-"}</span>
      ),
    },
    {
      title: "Approved",
      dataIndex: "approved",
      key: "approved",
      width: 110,
      align: "center" as const,
      render: (text: string) =>
        text === "Pending" || text === "---" ? (
          <Tag color="orange">{text}</Tag>
        ) : (
          <span>{text ? dayjs(text).format("DD-MMM-YYYY") : "-"}</span>
        ),
    },
    {
      title: "Paid",
      dataIndex: "paid",
      key: "paid",
      width: 110,
      align: "center" as const,
      render: (text: string) =>
        text === "---" ? (
          <Tag color="red">Pending</Tag>
        ) : (
          <span>{text ? dayjs(text).format("DD-MMM-YYYY") : "-"}</span>
        ),
    },
    {
      title: "Aging (Days)",
      dataIndex: "agingDays",
      key: "agingDays",
      width: 100,
      align: "center" as const,
      render: (val: number) => (
        <Tag color={val > 14 ? "red" : val > 7 ? "orange" : "green"}>{val}</Tag>
      ),
    },
    {
      title: "Interest On Delay",
      dataIndex: "interestCharged",
      key: "interestCharged",
      width: 130,
      align: "center" as const,
      render: (val: number, record: RABillLifecycle) => (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          <Tag color="orange">{record.interestRate}% per month</Tag>
          <span style={{ fontSize: 11, fontWeight: 600, color: "#fa8c16" }}>
            {val.toFixed(2)}% charged
          </span>
        </div>
      ),
    },
    {
      title: "Risk Impact (₹)",
      dataIndex: "riskImpact",
      key: "riskImpact",
      width: 130,
      align: "right" as const,
      render: (val: number, record: RABillLifecycle) => (
        <span
          style={{
            fontWeight: 700,
            color: "#ff4d4f",
            cursor: "pointer",
            textDecoration: "underline",
          }}
          onClick={(e) => {
            e.stopPropagation();
            setSelectedRABill(record);
            setRaRiskDrawerVisible(true);
          }}
        >
          {formatCurrency(val, "INR")}
        </span>
      ),
    },
    {
      title: "Flag",
      dataIndex: "flag",
      key: "flag",
      width: 80,
      align: "center" as const,
      render: (flag: string) => {
        const getTooltip = () => {
          if (flag === "🔴") return ">14 days approval pending";
          if (flag === "🟠") return "7-14 days approval pending";
          return "<7 days approval pending";
        };
        return (
          <Tooltip title={getTooltip()}>
            <span style={{ fontSize: 18, cursor: "help" }}>{flag}</span>
          </Tooltip>
        );
      },
    },
  ];

  const paymentColumns = [
    {
      title: "Contractor",
      dataIndex: "contractor",
      key: "contractor",
      width: 180,
      render: (text: string) => <span style={{ fontWeight: 600 }}>{text}</span>,
    },
    {
      title: "Total WOs",
      dataIndex: "totalWOs",
      key: "totalWOs",
      width: 100,
      align: "center" as const,
    },
    {
      title: "RA Submitted",
      dataIndex: "raSubmitted",
      key: "raSubmitted",
      width: 110,
      align: "center" as const,
    },
    {
      title: "Approved Value (₹)",
      dataIndex: "approvedValue",
      key: "approvedValue",
      width: 150,
      align: "right" as const,
      render: (val: number) => (
        <span style={{ fontWeight: 600 }}>{formatCurrency(val, "INR")}</span>
      ),
    },
    {
      title: "Paid (₹)",
      dataIndex: "paidValue",
      key: "paidValue",
      width: 140,
      align: "right" as const,
      render: (val: number) => (
        <span style={{ fontWeight: 600, color: "#52c41a" }}>
          {formatCurrency(val, "INR")}
        </span>
      ),
    },
    {
      title: "Outstanding (₹)",
      dataIndex: "outstanding",
      key: "outstanding",
      width: 140,
      align: "right" as const,
      render: (val: number) => (
        <span
          style={{
            fontWeight: 600,
            color: val > 1000000 ? "#ff4d4f" : "#faad14",
          }}
        >
          {formatCurrency(val, "INR")}
        </span>
      ),
    },
    {
      title: "% Paid",
      dataIndex: "percentPaid",
      key: "percentPaid",
      width: 120,
      render: (val: number) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Progress
            percent={val}
            size="small"
            strokeColor={
              val >= 90 ? "#52c41a" : val >= 75 ? "#faad14" : "#ff4d4f"
            }
            style={{ width: 70 }}
          />
          {/* <span style={{ fontSize: 12, fontWeight: 600 }}>{val.toFixed(1)}%</span> */}
        </div>
      ),
    },
    {
      title: "Avg Payment Delay (Days)",
      dataIndex: "avgPaymentDelay",
      key: "avgPaymentDelay",
      width: 130,
      align: "center" as const,
      render: (val: number) => (
        <Tag color={val > 15 ? "red" : val > 10 ? "orange" : "green"}>
          {val}
        </Tag>
      ),
    },
    {
      title: "Risk Impact (₹)",
      dataIndex: "riskImpact",
      key: "riskImpact",
      width: 140,
      align: "right" as const,
      render: (val: number, record: PaymentStatus) => (
        <span
          style={{
            fontWeight: 700,
            color: "#ff4d4f",
            cursor: "pointer",
            textDecoration: "underline",
          }}
          onClick={(e) => {
            e.stopPropagation();
            setSelectedPaymentStatus(record);
            setPaymentRiskDrawerVisible(true);
          }}
        >
          {formatCurrency(val, "INR")}
        </span>
      ),
    },
    {
      title: "Flag",
      dataIndex: "flag",
      key: "flag",
      width: 80,
      align: "center" as const,
      render: (flag: string) => {
        const getTooltip = () => {
          if (flag === "🔴") return ">15 days delay or >₹10L outstanding";
          if (flag === "🟠") return "Moderate payment delay or outstanding";
          return "Payment on time, <15 days delay";
        };
        return (
          <Tooltip title={getTooltip()}>
            <span style={{ fontSize: 18, cursor: "help" }}>{flag}</span>
          </Tooltip>
        );
      },
    },
  ];

  const retentionColumns = [
    {
      title: "WO #",
      dataIndex: "woNumber",
      key: "woNumber",
      width: 130,
      render: (text: string) => <span style={{ fontWeight: 600 }}>{text}</span>,
    },
    {
      title: "Contractor",
      dataIndex: "contractor",
      key: "contractor",
      width: 150,
    },
    {
      title: "Retention %",
      dataIndex: "retentionPercent",
      key: "retentionPercent",
      width: 100,
      align: "center" as const,
      render: (val: number) => <span>{val}%</span>,
    },
    {
      title: "Accrued (₹)",
      dataIndex: "accrued",
      key: "accrued",
      width: 130,
      align: "right" as const,
      render: (val: number) => <span>{formatCurrency(val, "INR")}</span>,
    },
    {
      title: "Released (₹)",
      dataIndex: "released",
      key: "released",
      width: 130,
      align: "right" as const,
      render: (val: number) => (
        <span style={{ color: "#52c41a" }}>{formatCurrency(val, "INR")}</span>
      ),
    },
    {
      title: "Balance (₹)",
      dataIndex: "balance",
      key: "balance",
      width: 130,
      align: "right" as const,
      render: (val: number) => (
        <span
          style={{
            fontWeight: 600,
            color: val > 200000 ? "#ff4d4f" : "#faad14",
          }}
        >
          {formatCurrency(val, "INR")}
        </span>
      ),
    },
    {
      title: "Next Release Date",
      dataIndex: "nextReleaseDate",
      key: "nextReleaseDate",
      width: 130,
      align: "center" as const,
      render: (text: string) => (
        <span>{text ? dayjs(text).format("DD-MMM-YYYY") : "-"}</span>
      ),
    },
    {
      title: "Flag",
      dataIndex: "flag",
      key: "flag",
      width: 80,
      align: "center" as const,
      render: (flag: string) => {
        const getTooltip = () => {
          if (flag === "🔴") return ">₹2L or >60 days post retention period";
          if (flag === "🟠") return "Retention pending release";
          return "Retention within release schedule";
        };
        return (
          <Tooltip title={getTooltip()}>
            <span style={{ fontSize: 18, cursor: "help" }}>{flag}</span>
          </Tooltip>
        );
      },
    },
  ];

  const budgetColumns = [
    {
      title: "Project",
      dataIndex: "project",
      key: "project",
      width: 150,
      render: (text: string) => <span style={{ fontWeight: 600 }}>{text}</span>,
    },
    {
      title: "Budget (₹)",
      dataIndex: "budget",
      key: "budget",
      width: 130,
      align: "right" as const,
      render: (val: number) => (
        <span style={{ fontWeight: 600 }}>{formatCurrency(val, "INR")}</span>
      ),
    },
    {
      title: "Committed (₹)",
      dataIndex: "committed",
      key: "committed",
      width: 140,
      align: "right" as const,
      render: (val: number) => <span>{formatCurrency(val, "INR")}</span>,
    },
    {
      title: "Accrued (₹)",
      dataIndex: "accrued",
      key: "accrued",
      width: 130,
      align: "right" as const,
      render: (val: number) => <span>{formatCurrency(val, "INR")}</span>,
    },
    {
      title: "Paid (₹)",
      dataIndex: "paid",
      key: "paid",
      width: 130,
      align: "right" as const,
      render: (val: number) => (
        <span style={{ color: "#52c41a" }}>{formatCurrency(val, "INR")}</span>
      ),
    },
    {
      title: "Variance (₹)",
      dataIndex: "variance",
      key: "variance",
      width: 130,
      align: "right" as const,
      render: (val: number) => (
        <span
          style={{
            fontWeight: 600,
            color: val < 0 ? "#ff4d4f" : "#faad14"
          }}
        >
          {formatCurrency(val, "INR")}
        </span>
      ),
    },
    {
      title: "Utilization %",
      dataIndex: "utilization",
      key: "utilization",
      width: 120,
      align: "center" as const,
      render: (val: number) => (
        <Tag color={val > 95 ? "red" : val > 85 ? "orange" : "green"}>
          {val}%
        </Tag>
      ),
    },
    {
      title: "Forecast Overrun (₹)",
      dataIndex: "forecastOverrun",
      key: "forecastOverrun",
      width: 160,
      align: "right" as const,
      render: (val: number) => (
        <span
          style={{
            fontWeight: 600,
            color: val > 1000000 ? "#ff4d4f" : val > 0 ? "#faad14" : "#52c41a"
          }}
        >
          {formatCurrency(val, "INR")}
        </span>
      ),
    },
    {
      title: "Flag",
      dataIndex: "flag",
      key: "flag",
      width: 80,
      align: "center" as const,
      render: (flag: string, record: BudgetPerformance) => {
        const getTooltip = () => {
          if (flag === "🔴") {
            if (record.variance < 0) return "Budget Overrun (Accrued > Budget)";
            if (record.forecastOverrun > record.variance) return "Forecast Overrun Risk";
            return "High Budget Risk";
          }
          if (flag === "🟠") return "Utilization >85% or Moderate Risk";
          return "Utilization <85%, Healthy Budget";
        };
        return (
          <Tooltip title={getTooltip()}>
            <span style={{ fontSize: 18, cursor: "help" }}>{flag}</span>
          </Tooltip>
        );
      },
    },
  ];

  // Calculate executive summary metrics
  const totalWOs = workOrderExecutions.length;
  // Total WO Value = Sum of woValue (1.2 Cr * 3 = 3.6 Cr)
  const totalWOsValue = workOrderExecutions.reduce((sum, wo) => sum + wo.woValue, 0);

  // Work Executed Value = Total WO Value × (Average of all WOs' Actual Progress %)
  const averageProgress = workOrderExecutions.reduce((sum, wo) => sum + wo.actualProgress, 0) / workOrderExecutions.length;
  const workExecutedValue = totalWOsValue * (averageProgress / 100);

  // RA Bills Approved Value = 80% of Work Executed Value
  const approvedRABills = raBillLifecycles.filter(
    (ra) => ra.approved !== "Pending" && ra.approved !== "---"
  );
  const approvedRACount = approvedRABills.length;
  const approvedRAValue = workExecutedValue * 0.80;

  const totalPaymentValue = paymentStatuses.reduce(
    (sum, p) => sum + p.paidValue,
    0
  );

  const delayedWOsData = workOrderExecutions.filter((wo) => wo.delayDays >= 3);
  const delayedWOsCount = delayedWOsData.length;
  const delayedWOsValue = delayedWOsData.reduce((sum, wo) => sum + wo.woValue, 0);

  const pendingRABills = raBillLifecycles.filter(
    (ra) => ra.approved === "Pending" || ra.approved === "---"
  );
  const pendingRACount = pendingRABills.length;
  const pendingRAValue = pendingRABills.reduce((sum, ra) => sum + ra.netPayable, 0);

  const totalOutstanding = paymentStatuses.reduce(
    (sum, p) => sum + p.outstanding,
    0
  );

  // Risk Impact = Sum of Execution Risk Impact + RA Bill Risk Impact
  const executionRiskImpact = workOrderExecutions.reduce((sum, wo) => sum + wo.riskImpact, 0);
  const raBillRiskImpact = raBillLifecycles.reduce((sum, ra) => sum + ra.riskImpact, 0);
  const totalRiskImpact = executionRiskImpact + raBillRiskImpact;
  const riskImpactPercent = totalWOsValue > 0 ? (totalRiskImpact / totalWOsValue) * 100 : 0;

  return (
    <>
      <Head title="Work Order Execution & Payment Risk Control Tower" />
      <Content>
        <BlockHead size="sm">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
          >
            <BlockHeadContent>
              <BlockTitle tag="h3" page>
                 Work Order Execution & Payment Risk Control Tower
              </BlockTitle>
              <p style={{ color: "#8c8c8c", marginTop: 8 }}>
                Integrated monitoring of execution progress, RA billing,
                payments, and financial exposure
              </p>
            </BlockHeadContent>
          </motion.div>
        </BlockHead>

        <Card className="card-bordered" style={{ overflow: "hidden" }}>
          <div className="card-aside-wrap" style={{ overflow: "hidden" }}>
            <div
              className="card-inner card-inner-lg"
              style={{ overflowX: "auto" }}
            >
              {/* Executive Summary */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                style={{ marginBottom: 32 }}
              >
                <h5 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
                   Executive Summary
                </h5>
                <div style={{ marginLeft: -8, marginRight: -8 }}>
                  {loading ? (
                    <Row gutter={[16, 16]}>
                      {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                        <Col key={i} xs={24} sm={12} lg={6}>
                          <ShimmerTableCard rows={1} />
                        </Col>
                      ))}
                    </Row>
                  ) : (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(4, 1fr)",
                        gap: "1px",
                        background: "#d9d9d9",
                      }}
                    >
                      {/* Row 1 */}
                      <div
                        style={{
                          padding: "24px 20px",
                          background: "#fff",
                          textAlign: "center",
                        }}
                      >
                        <motion.div
                          style={{
                            fontSize: 12,
                            marginBottom: 12,
                            textTransform: "uppercase",
                            letterSpacing: "1px",
                            fontWeight: 700,
                            background:
                              "linear-gradient(45deg, #1890ff, #722ed1, #13c2c2, #52c41a)",
                            backgroundSize: "300% 300%",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text",
                          }}
                          animate={{
                            backgroundPosition: [
                              "0% 50%",
                              "100% 50%",
                              "0% 50%",
                            ],
                          }}
                          transition={{
                            duration: 4,
                            ease: "linear",
                            repeat: Infinity,
                          }}
                        >
                          Total WOs
                        </motion.div>
                        <div
                          style={{
                            fontSize: 28,
                            fontWeight: 800,
                            color: "#1890ff",
                            lineHeight: 1,
                          }}
                        >
                          {formatCurrency(totalWOsValue, "INR")}
                        </div>
                      </div>

                      <div
                        style={{
                          padding: "24px 20px",
                          background: "#fff",
                          textAlign: "center",
                        }}
                      >
                        <motion.div
                          style={{
                            fontSize: 12,
                            marginBottom: 12,
                            textTransform: "uppercase",
                            letterSpacing: "1px",
                            fontWeight: 700,
                            background:
                              "linear-gradient(45deg, #1890ff, #722ed1, #13c2c2, #52c41a)",
                            backgroundSize: "300% 300%",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text",
                          }}
                          animate={{
                            backgroundPosition: [
                              "0% 50%",
                              "100% 50%",
                              "0% 50%",
                            ],
                          }}
                          transition={{
                            duration: 4,
                            ease: "linear",
                            repeat: Infinity,
                          }}
                        >
                          Work Executed
                        </motion.div>
                        <div
                          style={{
                            fontSize: 28,
                            fontWeight: 800,
                            color: "#13c2c2",
                            lineHeight: 1,
                          }}
                        >
                          {formatCurrency(workExecutedValue, "INR")}
                        </div>
                      </div>

                      <div
                        style={{
                          padding: "24px 20px",
                          background: "#fff",
                          textAlign: "center",
                        }}
                      >
                        <motion.div
                          style={{
                            fontSize: 12,
                            marginBottom: 12,
                            textTransform: "uppercase",
                            letterSpacing: "1px",
                            fontWeight: 700,
                            background:
                              "linear-gradient(45deg, #1890ff, #722ed1, #13c2c2, #52c41a)",
                            backgroundSize: "300% 300%",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text",
                          }}
                          animate={{
                            backgroundPosition: [
                              "0% 50%",
                              "100% 50%",
                              "0% 50%",
                            ],
                          }}
                          transition={{
                            duration: 4,
                            ease: "linear",
                            repeat: Infinity,
                          }}
                        >
                          RA Bills Approved
                        </motion.div>
                        <div
                          style={{
                            fontSize: 28,
                            fontWeight: 800,
                            color: "#52c41a",
                            lineHeight: 1,
                          }}
                        >
                          {formatCurrency(approvedRAValue, "INR")}
                        </div>
                      </div>

                      <div
                        style={{
                          padding: "24px 20px",
                          background: "#fff",
                          textAlign: "center",
                        }}
                      >
                        <motion.div
                          style={{
                            fontSize: 12,
                            marginBottom: 12,
                            textTransform: "uppercase",
                            letterSpacing: "1px",
                            fontWeight: 700,
                            background:
                              "linear-gradient(45deg, #1890ff, #722ed1, #13c2c2, #52c41a)",
                            backgroundSize: "300% 300%",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text",
                          }}
                          animate={{
                            backgroundPosition: [
                              "0% 50%",
                              "100% 50%",
                              "0% 50%",
                            ],
                          }}
                          transition={{
                            duration: 4,
                            ease: "linear",
                            repeat: Infinity,
                          }}
                        >
                          Total Paid
                        </motion.div>
                        <div
                          style={{
                            fontSize: 28,
                            fontWeight: 800,
                            color: "#52c41a",
                            lineHeight: 1,
                          }}
                        >
                          {formatCurrency(totalPaymentValue, "INR")}
                        </div>
                      </div>

                      {/* Row 2 */}
                      <div
                        style={{
                          padding: "24px 20px",
                          background: "#fff",
                          textAlign: "center",
                        }}
                      >
                        <motion.div
                          style={{
                            fontSize: 12,
                            marginBottom: 12,
                            textTransform: "uppercase",
                            letterSpacing: "1px",
                            fontWeight: 700,
                            background:
                              "linear-gradient(45deg, #1890ff, #722ed1, #13c2c2, #52c41a)",
                            backgroundSize: "300% 300%",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text",
                          }}
                          animate={{
                            backgroundPosition: [
                              "0% 50%",
                              "100% 50%",
                              "0% 50%",
                            ],
                          }}
                          transition={{
                            duration: 4,
                            ease: "linear",
                            repeat: Infinity,
                          }}
                        >
                          Delayed Work Orders
                        </motion.div>
                        <div
                          style={{
                            fontSize: 32,
                            fontWeight: 800,
                            color: "#cf1322",
                            lineHeight: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 10,
                          }}
                        >
                          {delayedWOsCount}
                          {delayedWOsCount > 0 && (
                            <WarningOutlined style={{ fontSize: 20 }} />
                          )}
                        </div>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: "#595959",
                            marginTop: 8,
                          }}
                        >
                          {formatCurrency(delayedWOsValue, "INR")}
                        </div>
                      </div>

                      <div
                        style={{
                          padding: "24px 20px",
                          background: "#fff",
                          textAlign: "center",
                        }}
                      >
                        <motion.div
                          style={{
                            fontSize: 12,
                            marginBottom: 12,
                            textTransform: "uppercase",
                            letterSpacing: "1px",
                            fontWeight: 700,
                            background:
                              "linear-gradient(45deg, #1890ff, #722ed1, #13c2c2, #52c41a)",
                            backgroundSize: "300% 300%",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text",
                          }}
                          animate={{
                            backgroundPosition: [
                              "0% 50%",
                              "100% 50%",
                              "0% 50%",
                            ],
                          }}
                          transition={{
                            duration: 4,
                            ease: "linear",
                            repeat: Infinity,
                          }}
                        >
                          RA Bills Pending
                        </motion.div>
                        <div
                          style={{
                            fontSize: 32,
                            fontWeight: 800,
                            color: "#fa8c16",
                            lineHeight: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 10,
                          }}
                        >
                          {pendingRACount}
                          {pendingRACount > 0 && (
                            <ClockCircleOutlined style={{ fontSize: 20 }} />
                          )}
                        </div>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: "#595959",
                            marginTop: 8,
                          }}
                        >
                          {formatCurrency(pendingRAValue, "INR")}
                        </div>
                      </div>

                      <div
                        style={{
                          padding: "24px 20px",
                          background: "#fff",
                          textAlign: "center",
                        }}
                      >
                        <motion.div
                          style={{
                            fontSize: 12,
                            marginBottom: 12,
                            textTransform: "uppercase",
                            letterSpacing: "1px",
                            fontWeight: 700,
                            background:
                              "linear-gradient(45deg, #1890ff, #722ed1, #13c2c2, #52c41a)",
                            backgroundSize: "300% 300%",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text",
                          }}
                          animate={{
                            backgroundPosition: [
                              "0% 50%",
                              "100% 50%",
                              "0% 50%",
                            ],
                          }}
                          transition={{
                            duration: 4,
                            ease: "linear",
                            repeat: Infinity,
                          }}
                        >
                          Outstanding Value
                        </motion.div>
                        <div
                          style={{
                            fontSize: 28,
                            fontWeight: 800,
                            color: "#cf1322",
                            lineHeight: 1,
                          }}
                        >
                          {formatCurrency(totalOutstanding, "INR")}
                        </div>
                      </div>

                      <div
                        style={{
                          padding: "24px 20px",
                          background: "#fff",
                          textAlign: "center",
                        }}
                      >
                        <motion.div
                          style={{
                            fontSize: 12,
                            marginBottom: 12,
                            textTransform: "uppercase",
                            letterSpacing: "1px",
                            fontWeight: 700,
                            background:
                              "linear-gradient(45deg, #1890ff, #722ed1, #13c2c2, #52c41a)",
                            backgroundSize: "300% 300%",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text",
                          }}
                          animate={{
                            backgroundPosition: [
                              "0% 50%",
                              "100% 50%",
                              "0% 50%",
                            ],
                          }}
                          transition={{
                            duration: 4,
                            ease: "linear",
                            repeat: Infinity,
                          }}
                        >
                          Risk Impact Value
                        </motion.div>
                        <div
                          style={{
                            fontSize: 28,
                            fontWeight: 800,
                            color: "#ff4d4f",
                            lineHeight: 1,
                          }}
                        >
                          {formatCurrency(totalRiskImpact, "INR")}
                        </div>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: "#595959",
                            marginTop: 8,
                          }}
                        >
                          {riskImpactPercent.toFixed(2)}% of WO Value
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>

              <Divider />

              {/* Execution Performance Matrix */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                style={{ marginBottom: 32 }}
              >
                <h5 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
                   Execution Performance Matrix
                </h5>
                {loading ? (
                  <ShimmerTableCard rows={3} />
                ) : (
                  <Table
                    columns={executionColumns}
                    dataSource={workOrderExecutions}
                    pagination={false}
                    bordered
                    size="small"
                    scroll={{ x: 1600 }}
                  />
                )}
              </motion.div>

              <Divider />

              {/* RA Bill Lifecycle Status */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
                style={{ marginBottom: 32 }}
              >
                <h5 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
                   RA Bill Lifecycle Status
                </h5>
                {loading ? (
                  <ShimmerTableCard rows={3} />
                ) : (
                  <Table
                    columns={raBillColumns}
                    dataSource={raBillLifecycles}
                    pagination={false}
                    bordered
                    size="small"
                    scroll={{ x: 1500 }}
                  />
                )}
              </motion.div>

              <Divider />

              {/* Payment Status & Outstanding Analysis */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.9 }}
                style={{ marginBottom: 32 }}
              >
                <h5 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
                   Payment Status & Outstanding Analysis
                </h5>
                {loading ? (
                  <ShimmerTableCard rows={3} />
                ) : (
                  <Table
                    columns={paymentColumns}
                    dataSource={paymentStatuses}
                    pagination={false}
                    bordered
                    size="small"
                    scroll={{ x: 1400 }}
                  />
                )}
              </motion.div>

              <Divider />

              {/* Retention & Exposure Register */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.1 }}
                style={{ marginBottom: 32 }}
              >
                <h5 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
                   Retention & Exposure Register
                </h5>
                {loading ? (
                  <ShimmerTableCard rows={3} />
                ) : (
                  <Table
                    columns={retentionColumns}
                    dataSource={retentionExposures}
                    pagination={false}
                    bordered
                    size="small"
                    scroll={{ x: 1200 }}
                  />
                )}
              </motion.div>

              <Divider />

              {/* Work Order Cost vs Budget Performance */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.3 }}
                style={{ marginBottom: 32 }}
              >
                <h5 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
                   Work Order Cost vs Budget Performance
                </h5>
                {loading ? (
                  <ShimmerTableCard rows={3} />
                ) : (
                  <Table
                    columns={budgetColumns}
                    dataSource={budgetPerformances}
                    pagination={false}
                    bordered
                    size="small"
                    scroll={{ x: 1200 }}
                  />
                )}
              </motion.div>

              <Divider />

              {/* Business Summary */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.5 }}
                style={{ marginTop: 32 }}
              >
                <h5 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
                   Business Summary
                </h5>

                <div
                  style={{
                    marginBottom: 20,
                    padding: "12px 16px",
                    background: "#fafafa",
                    borderRadius: 6,
                    borderLeft: "3px solid #faad14",
                  }}
                >
                  <p
                    style={{
                      fontSize: 13,
                      marginBottom: 0,
                      lineHeight: 1.7,
                      color: "#595959",
                    }}
                  >
                    <strong style={{ color: "#262626" }}>
                      3 active Work Orders
                    </strong>{" "}
                    (Total{" "}
                    <strong style={{ color: "#262626" }}>₹16.05L value</strong>)
                    → <strong style={{ color: "#cf1322" }}>2 delayed</strong> →{" "}
                    <strong style={{ color: "#cf1322" }}>
                      ₹4.8L work value at risk
                    </strong>
                  </p>
                </div>

                <div style={{ marginLeft: -8, marginRight: -8 }}>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} lg={12}>
                      <div
                        style={{
                          padding: "16px",
                          background: "#fff",
                          borderRadius: 6,
                          border: "1px solid #e8e8e8",
                        }}
                      >
                        <h6
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            marginBottom: 12,
                            color: "#262626",
                          }}
                        >
                          Critical Issues
                        </h6>
                        <ul
                          style={{ margin: 0, padding: 0, listStyle: "none" }}
                        >
                          <li
                            style={{
                              fontSize: 12,
                              color: "#595959",
                              marginBottom: 8,
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <WarningOutlined
                              style={{ color: "#ff4d4f", fontSize: 14 }}
                            />
                            <strong>2 RA bills pending approval</strong> → ₹6.1L
                            delayed billing
                          </li>
                          <li
                            style={{
                              fontSize: 12,
                              color: "#595959",
                              marginBottom: 8,
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <WarningOutlined
                              style={{ color: "#ff4d4f", fontSize: 14 }}
                            />
                            <strong>2 contractors cash flow strained</strong>{" "}
                            due to ₹22.2L pending payments
                          </li>
                          <li
                            style={{
                              fontSize: 12,
                              color: "#595959",
                              marginBottom: 0,
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <WarningOutlined
                              style={{ color: "#faad14", fontSize: 14 }}
                            />
                            <strong>1 project budget overrun</strong> ₹7L (10.4%
                            variance on Elysium Towers)
                          </li>
                        </ul>
                      </div>
                    </Col>
                    <Col xs={24} lg={12}>
                      <div
                        style={{
                          padding: "16px",
                          background: "#fff",
                          borderRadius: 6,
                          border: "1px solid #e8e8e8",
                        }}
                      >
                        <h6
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            marginBottom: 12,
                            color: "#262626",
                          }}
                        >
                          Key Actions Required
                        </h6>
                        <ul
                          style={{ margin: 0, padding: 0, listStyle: "none" }}
                        >
                          <li
                            style={{
                              fontSize: 12,
                              color: "#595959",
                              marginBottom: 8,
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <CheckCircleOutlined
                              style={{ color: "#52c41a", fontSize: 14 }}
                            />
                            Fast-track RA-00027 approval (15 days aging)
                          </li>
                          <li
                            style={{
                              fontSize: 12,
                              color: "#595959",
                              marginBottom: 8,
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <CheckCircleOutlined
                              style={{ color: "#52c41a", fontSize: 14 }}
                            />
                            Expedite material supply for WO-2025-0012 &
                            WO-2025-0067
                          </li>
                          <li
                            style={{
                              fontSize: 12,
                              color: "#595959",
                              marginBottom: 0,
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <CheckCircleOutlined
                              style={{ color: "#52c41a", fontSize: 14 }}
                            />
                            Release ₹6.95L retention balance (3 WOs pending)
                          </li>
                        </ul>
                      </div>
                    </Col>
                  </Row>
                </div>
              </motion.div>
            </div>
          </div>
        </Card>
      </Content>

      {/* WO Risk Interlinking Drawer */}
      <Drawer
        title={
          <span>
            <RobotOutlined style={{ marginRight: 8, color: "#1890ff" }} />
            Work Order Risk Interlinking & Automated Mitigation
          </span>
        }
        placement="right"
        onClose={() => setWoInterlinkingDrawerVisible(false)}
        open={woInterlinkingDrawerVisible}
        width="85%"
        style={{ maxWidth: 1400 }}
      >
        {selectedWORisks.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <Descriptions
              bordered
              column={2}
              size="small"
              style={{ marginBottom: 24 }}
            >
              <Descriptions.Item label="WO Number" span={2}>
                <strong>{selectedWORisks[0].woNumber}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Contractor">
                {selectedWORisks[0].contractor}
              </Descriptions.Item>
              <Descriptions.Item label="Delay Days">
                <Tag color="red" style={{ fontSize: 13, fontWeight: 600 }}>
                  {selectedWORisks[0].delayDays} days
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Risk Type" span={2}>
                <Tag color="orange" style={{ fontSize: 12 }}>
                  {selectedWORisks[0].riskType}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Downstream Impact" span={2}>
                {selectedWORisks[0].downstreamImpact}
              </Descriptions.Item>
              <Descriptions.Item label="Triggered Module" span={2}>
                {selectedWORisks[0].triggeredModule}
              </Descriptions.Item>
              <Descriptions.Item label="Monetary Impact" span={2}>
                <strong style={{ color: "#ff4d4f", fontSize: 14 }}>
                  {formatCurrency(selectedWORisks[0].monetaryImpact, "INR")}
                </strong>
              </Descriptions.Item>
            </Descriptions>

            <Divider>Work Order Delay with Risk Interlinking</Divider>

            <Table
              columns={[
                {
                  title: "WO #",
                  dataIndex: "woNumber",
                  key: "woNumber",
                  width: 130,
                  render: (text: string) => (
                    <span style={{ fontWeight: 600 }}>{text}</span>
                  ),
                },
                {
                  title: "Contractor",
                  dataIndex: "contractor",
                  key: "contractor",
                  width: 150,
                },
                {
                  title: "Delay (Days)",
                  dataIndex: "delayDays",
                  key: "delayDays",
                  width: 100,
                  align: "center" as const,
                  render: (val: number) => <Tag color="red">{val}</Tag>,
                },
                {
                  title: "Risk Type",
                  dataIndex: "riskType",
                  key: "riskType",
                  width: 180,
                  render: (text: string) => (
                    <Tag color="orange" style={{ fontSize: 11 }}>
                      {text}
                    </Tag>
                  ),
                },
                {
                  title: "Downstream Impact",
                  dataIndex: "downstreamImpact",
                  key: "downstreamImpact",
                  width: 250,
                },
                {
                  title: "Monetary Impact",
                  dataIndex: "monetaryImpact",
                  key: "monetaryImpact",
                  width: 130,
                  align: "right" as const,
                  render: (val: number) => (
                    <span style={{ fontWeight: 600, color: "#ff4d4f" }}>
                      {formatCurrency(val, "INR")}
                    </span>
                  ),
                },
                {
                  title: "Responsible Person",
                  dataIndex: "responsiblePerson",
                  key: "responsiblePerson",
                  width: 140,
                },
                {
                  title: "Status",
                  dataIndex: "status",
                  key: "status",
                  width: 100,
                  render: (status: string) => (
                    <Tag
                      color={
                        status === "Active"
                          ? "red"
                          : status === "Mitigated"
                          ? "green"
                          : "orange"
                      }
                    >
                      {status}
                    </Tag>
                  ),
                },
              ]}
              dataSource={selectedWORisks}
              rowKey="woId"
              pagination={false}
              scroll={{ x: 1300 }}
              expandable={{
                expandedRowKeys: expandedRowKeys,
                onExpand: (expanded, record) => {
                  if (expanded) {
                    setExpandedRowKeys([record.woId]);
                    setAiLoadingWOs((prev) => ({
                      ...prev,
                      [record.woId]: true,
                    }));
                    setAiLoadingTexts((prev) => ({
                      ...prev,
                      [record.woId]: aiLoadingMessages[0],
                    }));

                    let messageIndex = 0;
                    const messageInterval = setInterval(() => {
                      messageIndex =
                        (messageIndex + 1) % aiLoadingMessages.length;
                      setAiLoadingTexts((prev) => ({
                        ...prev,
                        [record.woId]: aiLoadingMessages[messageIndex],
                      }));
                    }, 800);

                    setTimeout(() => {
                      clearInterval(messageInterval);
                      setAiLoadingWOs((prev) => ({
                        ...prev,
                        [record.woId]: false,
                      }));
                    }, 2500);
                  } else {
                    setExpandedRowKeys([]);
                  }
                },
                expandedRowRender: (record) => (
                  <div
                    style={{
                      padding: "16px",
                      background: "#fafafa",
                      borderRadius: 8,
                    }}
                  >
                    <h6
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        marginBottom: 12,
                        color: "#262626",
                      }}
                    >
                      <RobotOutlined
                        style={{ marginRight: 6, color: "#1890ff" }}
                      />
                      Automated Mitigation
                    </h6>

                    {aiLoadingWOs[record.woId] ? (
                      <div style={{ textAlign: "center", padding: "32px 0" }}>
                        <Spin
                          indicator={
                            <LoadingOutlined
                              style={{ fontSize: 32, color: "#1890ff" }}
                              spin
                            />
                          }
                        />
                        <p
                          style={{
                            marginTop: 16,
                            color: "#8c8c8c",
                            fontSize: 13,
                          }}
                        >
                          {aiLoadingTexts[record.woId] || "Processing..."}
                        </p>
                      </div>
                    ) : (
                      <>
                        <div
                          style={{
                            marginBottom: 16,
                            padding: "12px 16px",
                            background: "#e6f7ff",
                            borderRadius: 6,
                            borderLeft: "3px solid #1890ff",
                          }}
                        >
                          <p
                            style={{
                              fontSize: 13,
                              marginBottom: 0,
                              lineHeight: 1.6,
                              color: "#262626",
                            }}
                          >
                            <strong>Strategy:</strong>{" "}
                            {record.automatedMitigation}
                          </p>
                        </div>

                        {record.automatedMitigationSteps ? (
                          <div style={{ marginTop: 8 }}>
                            {record.automatedMitigationSteps.map(
                              (stepData, index) => (
                                <div
                                  key={index}
                                  style={{
                                    marginBottom: 16,
                                    paddingBottom: 12,
                                    borderBottom:
                                      index <
                                      record.automatedMitigationSteps!.length -
                                        1
                                        ? "1px dashed #e8e8e8"
                                        : "none",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      marginBottom: 8,
                                      gap: 8,
                                    }}
                                  >
                                    <strong
                                      style={{ fontSize: 13, color: "#262626" }}
                                    >
                                      {stepData.step}
                                    </strong>
                                  </div>

                                  {stepData.details && (
                                    <ul
                                      style={{
                                        margin: "8px 0 0 24px",
                                        padding: 0,
                                        listStyle: "disc",
                                      }}
                                    >
                                      {stepData.details.map((detail, idx) => (
                                        <li
                                          key={idx}
                                          style={{
                                            fontSize: 12,
                                            color: "#595959",
                                            marginBottom: 4,
                                            lineHeight: 1.5,
                                          }}
                                        >
                                          {detail}
                                        </li>
                                      ))}
                                    </ul>
                                  )}

                                  {stepData.notifications && (
                                    <div
                                      style={{ marginTop: 8, marginLeft: 24 }}
                                    >
                                      <div
                                        style={{
                                          fontSize: 12,
                                          fontWeight: 600,
                                          color: "#595959",
                                          marginBottom: 6,
                                        }}
                                      >
                                        Actions & Notifications
                                      </div>
                                      <ul
                                        style={{
                                          margin: 0,
                                          padding: 0,
                                          listStyle: "none",
                                        }}
                                      >
                                        {stepData.notifications.map(
                                          (notification, idx) => (
                                            <li
                                              key={idx}
                                              style={{
                                                fontSize: 12,
                                                color: "#595959",
                                                marginBottom: 6,
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 6,
                                              }}
                                            >
                                              <BellOutlined
                                                style={{
                                                  fontSize: 12,
                                                  color: "#1890ff",
                                                }}
                                              />
                                              {notification}
                                            </li>
                                          )
                                        )}
                                      </ul>
                                    </div>
                                  )}

                                  {stepData.costSavings && (
                                    <div
                                      style={{
                                        marginTop: 8,
                                        marginLeft: 24,
                                        padding: "6px 12px",
                                        background: "#f6ffed",
                                        borderRadius: 4,
                                        border: "1px solid #b7eb8f",
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: 12,
                                          fontWeight: 600,
                                          color: "#52c41a",
                                        }}
                                      >
                                        💰 Cost Savings: {stepData.costSavings}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              )
                            )}
                          </div>
                        ) : (
                          <p
                            style={{
                              fontSize: 13,
                              color: "#8c8c8c",
                              marginTop: 8,
                            }}
                          >
                            {record.automatedMitigation}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                ),
              }}
            />
          </motion.div>
        )}
      </Drawer>

      {/* RA Bill Risk Mitigation Drawer */}
      <Drawer
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <WarningOutlined style={{ color: "#ff4d4f", fontSize: 18 }} />
            <span>RA Bill Payment Risk & Automated Mitigation</span>
          </div>
        }
        placement="right"
        width={900}
        onClose={() => setRaRiskDrawerVisible(false)}
        open={raRiskDrawerVisible}
      >
        {selectedRABill && (
          <div>
            {/* RA Bill Summary */}
            <div
              style={{
                padding: "16px",
                background: "#fff7e6",
                borderRadius: 8,
                marginBottom: 24,
                border: "1px solid #ffd591",
              }}
            >
              <h6 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
                RA Bill Summary
              </h6>
              <Row gutter={[16, 12]}>
                <Col span={8}>
                  <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 4 }}>
                    RA Number
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#262626" }}>
                    {selectedRABill.raNumber}
                  </div>
                </Col>
                <Col span={8}>
                  <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 4 }}>
                    WO Number
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#262626" }}>
                    {selectedRABill.woNumber}
                  </div>
                </Col>
                <Col span={8}>
                  <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 4 }}>
                    Contractor
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#262626" }}>
                    {selectedRABill.contractor}
                  </div>
                </Col>
                <Col span={8}>
                  <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 4 }}>
                    Net Payable
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#52c41a" }}>
                    {formatCurrency(selectedRABill.netPayable, "INR")}
                  </div>
                </Col>
                <Col span={8}>
                  <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 4 }}>
                    Aging Days
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#fa8c16" }}>
                    {selectedRABill.agingDays} days
                  </div>
                </Col>
                <Col span={8}>
                  <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 4 }}>
                    Interest Charged
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#fa8c16" }}>
                    {selectedRABill.interestCharged.toFixed(2)}%
                  </div>
                </Col>
              </Row>
            </div>

            {/* Risk Impact Calculation */}
            <div
              style={{
                padding: "16px",
                background: "#fff1f0",
                borderRadius: 8,
                marginBottom: 24,
                border: "1px solid #ffccc7",
              }}
            >
              <h6 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: "#cf1322" }}>
                💰 Financial Risk Impact
              </h6>
              <p style={{ fontSize: 13, color: "#595959", marginBottom: 8 }}>
                <strong>Calculation:</strong> Net Payable × Interest Charged (based on aging days)
              </p>
              <p style={{ fontSize: 13, color: "#595959", marginBottom: 8 }}>
                = {formatCurrency(selectedRABill.netPayable, "INR")} × {selectedRABill.interestCharged.toFixed(2)}%
              </p>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: "#cf1322",
                  marginTop: 12,
                }}
              >
                Risk Impact: {formatCurrency(selectedRABill.riskImpact, "INR")}
              </div>
            </div>

            {/* Automated Mitigation Steps */}
            <div
              style={{
                padding: "16px",
                background: "#f0f5ff",
                borderRadius: 8,
                border: "1px solid #adc6ff",
              }}
            >
              <h6
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  marginBottom: 16,
                  color: "#262626",
                }}
              >
                <RobotOutlined style={{ marginRight: 6, color: "#1890ff" }} />
                Automated Mitigation Strategy
              </h6>

              {aiLoadingRABill ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "24px 0",
                    justifyContent: "center",
                  }}
                >
                  <Spin
                    indicator={
                      <LoadingOutlined
                        style={{ fontSize: 18, color: "#1890ff" }}
                        spin
                      />
                    }
                  />
                  <motion.span
                    key={aiLoadingRAText}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0 }}
                    style={{
                      fontSize: 13,
                      color: "#1890ff",
                      fontStyle: "italic",
                    }}
                  >
                    {aiLoadingRAText}
                  </motion.span>
                </div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  {/* Step 1: Fast-track Approval */}
                  <div
                    style={{
                      marginBottom: 16,
                      paddingBottom: 16,
                      borderBottom: "1px dashed #e8e8e8",
                    }}
                  >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 8,
                    gap: 8,
                  }}
                >
                  <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 14 }} />
                  <strong style={{ fontSize: 13, color: "#262626" }}>
                    Step 1: Fast-Track Approval Process
                  </strong>
                </div>
                <ul style={{ margin: "8px 0 0 24px", padding: 0, listStyle: "disc" }}>
                  <li style={{ fontSize: 12, color: "#595959", marginBottom: 4, lineHeight: 1.5 }}>
                    Auto-escalate RA bill to CFO dashboard with priority flag
                  </li>
                  <li style={{ fontSize: 12, color: "#595959", marginBottom: 4, lineHeight: 1.5 }}>
                    Bypass standard approval workflow if aging &gt; 14 days
                  </li>
                  <li style={{ fontSize: 12, color: "#595959", marginBottom: 4, lineHeight: 1.5 }}>
                    Trigger notification to Finance Head: "RA {selectedRABill.raNumber} pending approval for {selectedRABill.agingDays} days - Interest accumulating at {selectedRABill.interestCharged.toFixed(2)}%"
                  </li>
                </ul>
                <div style={{ marginTop: 8, marginLeft: 24 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#595959", marginBottom: 6 }}>
                    Notifications
                  </div>
                  <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                    <li style={{ fontSize: 12, color: "#595959", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                      <BellOutlined style={{ fontSize: 12, color: "#1890ff" }} />
                      Alert CFO - High Priority RA Approval Required
                    </li>
                    <li style={{ fontSize: 12, color: "#595959", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                      <BellOutlined style={{ fontSize: 12, color: "#1890ff" }} />
                      Notify Finance Manager - Payment delay causing {formatCurrency(selectedRABill.riskImpact, "INR")} interest charge
                    </li>
                  </ul>
                </div>
              </div>

              {/* Step 2: Payment Priority Queue */}
              <div
                style={{
                  marginBottom: 16,
                  paddingBottom: 16,
                  borderBottom: "1px dashed #e8e8e8",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 8,
                    gap: 8,
                  }}
                >
                  <DollarOutlined style={{ color: "#52c41a", fontSize: 14 }} />
                  <strong style={{ fontSize: 13, color: "#262626" }}>
                    Step 2: Prioritize in Payment Queue
                  </strong>
                </div>
                <ul style={{ margin: "8px 0 0 24px", padding: 0, listStyle: "disc" }}>
                  <li style={{ fontSize: 12, color: "#595959", marginBottom: 4, lineHeight: 1.5 }}>
                    Move RA to top of payment queue to minimize interest accumulation
                  </li>
                  <li style={{ fontSize: 12, color: "#595959", marginBottom: 4, lineHeight: 1.5 }}>
                    Schedule payment within 3 business days to prevent further delay
                  </li>
                  <li style={{ fontSize: 12, color: "#595959", marginBottom: 4, lineHeight: 1.5 }}>
                    Auto-reserve funds from liquidity pool for immediate disbursement
                  </li>
                </ul>
                <div
                  style={{
                    marginTop: 8,
                    marginLeft: 24,
                    padding: "6px 12px",
                    background: "#f6ffed",
                    borderRadius: 4,
                    border: "1px solid #b7eb8f",
                  }}
                >
                  <span style={{ fontSize: 12, fontWeight: 600, color: "#52c41a" }}>
                    💰 Estimated Savings: Prevent {formatCurrency(selectedRABill.riskImpact * 1.5, "INR")} additional interest if resolved within 3 days
                  </span>
                </div>
              </div>

              {/* Step 3: Contractor Communication */}
              <div style={{ marginBottom: 0 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 8,
                    gap: 8,
                  }}
                >
                  <FileTextOutlined style={{ color: "#52c41a", fontSize: 14 }} />
                  <strong style={{ fontSize: 13, color: "#262626" }}>
                    Step 3: Contractor Communication & Relationship Management
                  </strong>
                </div>
                <ul style={{ margin: "8px 0 0 24px", padding: 0, listStyle: "disc" }}>
                  <li style={{ fontSize: 12, color: "#595959", marginBottom: 4, lineHeight: 1.5 }}>
                    Auto-send payment confirmation to contractor: "{selectedRABill.contractor}"
                  </li>
                  <li style={{ fontSize: 12, color: "#595959", marginBottom: 4, lineHeight: 1.5 }}>
                    Provide updated payment timeline and expected disbursement date
                  </li>
                  <li style={{ fontSize: 12, color: "#595959", marginBottom: 4, lineHeight: 1.5 }}>
                    Maintain contractor trust and ensure continued site progress on {selectedRABill.woNumber}
                  </li>
                </ul>
              </div>
                </motion.div>
              )}
            </div>
          </div>
        )}
      </Drawer>

      {/* Payment Status Risk Drawer - RA Bill Wise Mitigation */}
      <Drawer
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <DollarOutlined style={{ color: "#ff4d4f", fontSize: 18 }} />
            <span>Payment Risk Analysis - RA Bill Wise Mitigation</span>
          </div>
        }
        placement="right"
        width={1000}
        onClose={() => setPaymentRiskDrawerVisible(false)}
        open={paymentRiskDrawerVisible}
      >
        {selectedPaymentStatus && (
          <div>
            {/* Contractor Summary */}
            <div
              style={{
                padding: "16px",
                background: "#fff7e6",
                borderRadius: 8,
                marginBottom: 24,
                border: "1px solid #ffd591",
              }}
            >
              <h6 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>
                Contractor Payment Risk Summary
              </h6>
              <Row gutter={[16, 12]}>
                <Col span={8}>
                  <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 4 }}>
                    Contractor
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#262626" }}>
                    {selectedPaymentStatus.contractor}
                  </div>
                </Col>
                <Col span={8}>
                  <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 4 }}>
                    RA Bills Submitted
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#262626" }}>
                    {selectedPaymentStatus.raSubmitted}
                  </div>
                </Col>
                <Col span={8}>
                  <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 4 }}>
                    Outstanding Amount
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#fa8c16" }}>
                    {formatCurrency(selectedPaymentStatus.outstanding, "INR")}
                  </div>
                </Col>
                <Col span={8}>
                  <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 4 }}>
                    Average Payment Delay
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#fa8c16" }}>
                    {selectedPaymentStatus.avgPaymentDelay} days
                  </div>
                </Col>
                <Col span={8}>
                  <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 4 }}>
                    Total Risk Impact
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#cf1322" }}>
                    {formatCurrency(selectedPaymentStatus.riskImpact, "INR")}
                  </div>
                </Col>
                <Col span={8}>
                  <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 4 }}>
                    Payment Performance
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: selectedPaymentStatus.percentPaid >= 90 ? "#52c41a" : selectedPaymentStatus.percentPaid >= 75 ? "#fa8c16" : "#cf1322" }}>
                    {selectedPaymentStatus.percentPaid.toFixed(1)}% Paid
                  </div>
                </Col>
              </Row>
            </div>

            {/* RA Bills List with Individual Risk Impact */}
            <div
              style={{
                padding: "16px",
                background: "#fff1f0",
                borderRadius: 8,
                marginBottom: 24,
                border: "1px solid #ffccc7",
              }}
            >
              <h6 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16, color: "#262626" }}>
                📋 RA Bills & Interest Charges ({selectedPaymentStatus.raBills.length} bills)
              </h6>

              {selectedPaymentStatus.raBills.map((raBill, index) => (
                <div
                  key={raBill.key}
                  style={{
                    marginBottom: index < selectedPaymentStatus.raBills.length - 1 ? 20 : 0,
                    paddingBottom: index < selectedPaymentStatus.raBills.length - 1 ? 20 : 0,
                    borderBottom: index < selectedPaymentStatus.raBills.length - 1 ? "1px dashed #ffccc7" : "none",
                  }}
                >
                  <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
                    <Col span={6}>
                      <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 4 }}>RA Number</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#262626" }}>{raBill.raNumber}</div>
                    </Col>
                    <Col span={6}>
                      <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 4 }}>WO Number</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#262626" }}>{raBill.woNumber}</div>
                    </Col>
                    <Col span={6}>
                      <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 4 }}>Net Payable</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#52c41a" }}>{formatCurrency(raBill.netPayable, "INR")}</div>
                    </Col>
                    <Col span={6}>
                      <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 4 }}>Aging Days</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#fa8c16" }}>{raBill.agingDays} days</div>
                    </Col>
                  </Row>
                  <Row gutter={[12, 12]}>
                    <Col span={8}>
                      <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 4 }}>Interest Rate</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#fa8c16" }}>{raBill.interestRate}% per month</div>
                    </Col>
                    <Col span={8}>
                      <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 4 }}>Interest Charged</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#fa8c16" }}>{raBill.interestCharged.toFixed(2)}%</div>
                    </Col>
                    <Col span={8}>
                      <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 4 }}>Risk Impact</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#cf1322" }}>{formatCurrency(raBill.riskImpact, "INR")}</div>
                    </Col>
                  </Row>
                </div>
              ))}
            </div>

            {/* Automated Mitigation Strategy */}
            <div
              style={{
                padding: "16px",
                background: "#f0f5ff",
                borderRadius: 8,
                border: "1px solid #adc6ff",
              }}
            >
              <h6
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                  marginBottom: 16,
                  color: "#262626",
                }}
              >
                <RobotOutlined style={{ marginRight: 6, color: "#1890ff" }} />
                Automated Mitigation Strategy for All RA Bills
              </h6>

              {aiLoadingPayment ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "24px 0",
                    justifyContent: "center",
                  }}
                >
                  <Spin
                    indicator={
                      <LoadingOutlined
                        style={{ fontSize: 18, color: "#1890ff" }}
                        spin
                      />
                    }
                  />
                  <motion.span
                    key={aiLoadingPaymentText}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0 }}
                    style={{
                      fontSize: 13,
                      color: "#1890ff",
                      fontStyle: "italic",
                    }}
                  >
                    {aiLoadingPaymentText}
                  </motion.span>
                </div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  {/* Step 1: Bulk Fast-track Approval */}
                  <div
                    style={{
                      marginBottom: 16,
                      paddingBottom: 16,
                      borderBottom: "1px dashed #e8e8e8",
                    }}
                  >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 8,
                    gap: 8,
                  }}
                >
                  <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 14 }} />
                  <strong style={{ fontSize: 13, color: "#262626" }}>
                    Step 1: Bulk Fast-Track Approval Process
                  </strong>
                </div>
                <ul style={{ margin: "8px 0 0 24px", padding: 0, listStyle: "disc" }}>
                  <li style={{ fontSize: 12, color: "#595959", marginBottom: 4, lineHeight: 1.5 }}>
                    Escalate all {selectedPaymentStatus.raSubmitted} RA bills to CFO dashboard with priority flag
                  </li>
                  <li style={{ fontSize: 12, color: "#595959", marginBottom: 4, lineHeight: 1.5 }}>
                    Create consolidated approval request for contractor: {selectedPaymentStatus.contractor}
                  </li>
                  <li style={{ fontSize: 12, color: "#595959", marginBottom: 4, lineHeight: 1.5 }}>
                    Bypass standard workflow for high-aging RA bills to prevent further interest accumulation
                  </li>
                </ul>
                <div style={{ marginTop: 8, marginLeft: 24 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#595959", marginBottom: 6 }}>
                    Notifications
                  </div>
                  <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                    <li style={{ fontSize: 12, color: "#595959", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                      <BellOutlined style={{ fontSize: 12, color: "#1890ff" }} />
                      Alert CFO - {selectedPaymentStatus.raSubmitted} RA bills from {selectedPaymentStatus.contractor} requiring immediate approval
                    </li>
                    <li style={{ fontSize: 12, color: "#595959", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                      <BellOutlined style={{ fontSize: 12, color: "#1890ff" }} />
                      Notify Finance Manager - Total interest impact: {formatCurrency(selectedPaymentStatus.riskImpact, "INR")}
                    </li>
                  </ul>
                </div>
              </div>

              {/* Step 2: Payment Queue Optimization */}
              <div
                style={{
                  marginBottom: 16,
                  paddingBottom: 16,
                  borderBottom: "1px dashed #e8e8e8",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 8,
                    gap: 8,
                  }}
                >
                  <DollarOutlined style={{ color: "#52c41a", fontSize: 14 }} />
                  <strong style={{ fontSize: 13, color: "#262626" }}>
                    Step 2: Optimize Payment Queue Priority
                  </strong>
                </div>
                <ul style={{ margin: "8px 0 0 24px", padding: 0, listStyle: "disc" }}>
                  <li style={{ fontSize: 12, color: "#595959", marginBottom: 4, lineHeight: 1.5 }}>
                    Prioritize payment of {formatCurrency(selectedPaymentStatus.outstanding, "INR")} outstanding amount
                  </li>
                  <li style={{ fontSize: 12, color: "#595959", marginBottom: 4, lineHeight: 1.5 }}>
                    Schedule staggered payments based on aging priority (oldest RA bills first)
                  </li>
                  <li style={{ fontSize: 12, color: "#595959", marginBottom: 4, lineHeight: 1.5 }}>
                    Auto-reserve liquidity from cash flow pool for contractor payments
                  </li>
                </ul>
                <div
                  style={{
                    marginTop: 8,
                    marginLeft: 24,
                    padding: "6px 12px",
                    background: "#f6ffed",
                    borderRadius: 4,
                    border: "1px solid #b7eb8f",
                  }}
                >
                  <span style={{ fontSize: 12, fontWeight: 600, color: "#52c41a" }}>
                    💰 Estimated Savings: Prevent {formatCurrency(selectedPaymentStatus.riskImpact * 2, "INR")} additional interest if all bills resolved within 5 days
                  </span>
                </div>
              </div>

              {/* Step 3: Contractor Relationship Management */}
              <div style={{ marginBottom: 0 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 8,
                    gap: 8,
                  }}
                >
                  <FileTextOutlined style={{ color: "#52c41a", fontSize: 14 }} />
                  <strong style={{ fontSize: 13, color: "#262626" }}>
                    Step 3: Contractor Communication & Trust Building
                  </strong>
                </div>
                <ul style={{ margin: "8px 0 0 24px", padding: 0, listStyle: "disc" }}>
                  <li style={{ fontSize: 12, color: "#595959", marginBottom: 4, lineHeight: 1.5 }}>
                    Send consolidated payment schedule to {selectedPaymentStatus.contractor}
                  </li>
                  <li style={{ fontSize: 12, color: "#595959", marginBottom: 4, lineHeight: 1.5 }}>
                    Provide timeline for each RA bill payment with expected disbursement dates
                  </li>
                  <li style={{ fontSize: 12, color: "#595959", marginBottom: 4, lineHeight: 1.5 }}>
                    Maintain contractor liquidity for continued progress on {selectedPaymentStatus.totalWOs} active work orders
                  </li>
                  <li style={{ fontSize: 12, color: "#595959", marginBottom: 4, lineHeight: 1.5 }}>
                    Set up auto-reminders for payment milestones to prevent future delays
                  </li>
                </ul>
              </div>
                </motion.div>
              )}
            </div>
          </div>
        )}
      </Drawer>
    </>
  );
};

export default WorkOrderExecutionPaymentControlTower;
